import { MutationCache, QueryClient } from '@tanstack/vue-query';

const ONE_MINUTE = 60 * 1000;

const tanStackQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Defaulting staleTime to `Infinity` so that background query refreshes are opt-in. This will prevent
      // unexepected UI behavior caused by queries refreshing in the background on a user without the developer
      // being aware. Also, this will prevent any issues during an add/edit flow where a background refresh
      // could cause unsaved form data to be trashed. Lastly, developers have control over forcing a refresh of
      // a query by using queryClient.invalidateQueries(), such as after a mutation or in a Vue lifecycle hook.
      //
      // Note: setting this to `Infinity` disables the refetchOnWindowFocus, refetchOnReconnect, refetchOnMount, and refetchInterval options.
      //
      // See: https://tkdodo.eu/blog/react-query-and-forms#no-background-updates
      staleTime: Infinity,

      // Any queries that are not in use for the specified gcTime will be garbage collected to free up memory.
      gcTime: 5 * ONE_MINUTE,

      // Retrying HTTP requests is not an ideal solution; instead, developers should expect all well-formed requests
      // to succeed and handle expected errors with proper UI feedback and error reporting (see initGlobalErrorHandlers below).
      // Unexpected errors should not be silenced as they are probably bugs.
      retry: false,
      retryOnMount: false,

      // The initGlobalErrorHandlers function will catch all uncaught or re-thrown errors. For http errors, it will display
      // a toast notification with the error message; for non-HTTP errors, it will send the error to Sentry. To prevent a
      // toast notification from being displayed for a specific error, use the preventErrorNotification(error) function.
      throwOnError: true,
    },
    mutations: {
      // The initGlobalErrorHandlers function will catch all uncaught or re-thrown errors. For http errors, it will display
      // a toast notification with the error message; for non-HTTP errors, it will send the error to Sentry. To prevent a
      // toast notification from being displayed for a specific error, use the preventErrorNotification(error) function.
      throwOnError: true,
    },
  },
  mutationCache: new MutationCache(), // TODO: consider using the onSuccess hook to automatically invalidate queries
});

export default tanStackQueryClient;
